import React, { useState, useEffect, useCallback, useRef } from "react";
import { Grid, TextFieldProps, Chip } from "@mui/material";
import MxInputField from "./MxInputField";
import "./MxInputChip.css"; // Import CSS file for animations
import MxTypography from "@rf-l1/MxTypography";

type types = Omit<TextFieldProps<"outlined">, "variant" | "onChange">;
export interface MxInputChipProps extends types {
  name?: string;
  inputRef?: any;
  backgroundColor?: string;
  value?: string[];
  onChange?: any;
}

const MxInputChip = (props: MxInputChipProps) => {
  const [message, setMessage] = useState("");
  const { onChange, name, value = [], ...rest } = props;
  const [chips, setChips] = useState<string[]>(value);

  useEffect(() => {
    setChips(MxChipInputHelpAdapterValue(value));
  }, [value]);

  const handleAddChip = useCallback(
    (chip: string) => {
      const newChips = [...chips, chip];
      setChips(newChips);
      if (onChange) onChange({ target: { name, value: newChips } });
    },
    [chips, onChange, name]
  );

  const handleRemoveChip = useCallback(
    (chipToRemove: string) => {
      const newChips = chips.filter(chip => chip !== chipToRemove);
      setChips(newChips);
      if (onChange) onChange({ target: { name, value: newChips } });
    },
    [chips, onChange, name]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement;
      if (
        (event.key === "Enter" ||
          event.key === "," ||
          event.key === " " ||
          event.key === "Tab") &&
        input.value.length > 0
      ) {
        event.preventDefault();
        handleAddChip(input.value);
        input.value = "";
      }
    },
    [handleAddChip]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement;
      props.onBlur?.(event);
      if (input?.value?.length > 0) {
        if (input.value.trim() !== "") {
          handleAddChip(input.value);

          input.value = "";
        }
      }
    },
    [handleAddChip]
  );
  const handleKeyUpCapture = (event: any) => {
    if (event?.target?.value?.length > 3) {
      setMessage("Para agregar un elemento presiona Enter, Coma, Espacio o Tab");
    } else {
      setMessage("");
    }
  };
  return (
    <Grid container gap={1} data-testid={`${props.id || props.name}-input-chip`}>
      <Grid item xs={12} data-testid={`${props.id || props.name}-input-chip-field`}>
        <MxInputField
          {...rest}
          id={props.id}
          data-testid={`${props.id || props.name}-input-chip-field-input`}
          onKeyDown={handleKeyDown}
          onKeyUpCapture={handleKeyUpCapture}
          // ...rest of the props...
        />
        {message?.length > 0 && (
          <MxTypography
            variant="overline"
            color="grey.700"
            ml={1.5}
            mt={0.5}
            sx={{ fontSize: "10.5px !important" }}
            data-testid={`${props.id || props.name}-input-chip-field-message`}
          >
            {message}
          </MxTypography>
        )}
      </Grid>
      <Grid item xs={12}>
        <Grid
          container
          spacing={1}
          data-testid={`${props.id || props.name}-input-chip-field-chips`}
        >
          {chips.map((chip, index) => (
            <Grid
              item
              key={index}
              data-testid={`${
                props.id || props.name
              }-input-chip-field-chip-${index}`}
              style={{
                transition: "opacity 300ms, transform 300ms",
                opacity: 1,
                transform: "scale(1)"
              }}
            >
              <Chip
                label={chip}
                onDelete={() => handleRemoveChip(chip)}
                data-testid={`${
                  props.id || props.name
                }-input-chip-field-chip-${index}-delete`}
                style={{
                  transition: "opacity 300ms, transform 300ms",
                  opacity: 1,
                  transform: "scale(1)"
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MxInputChip;
const _transform = (value: string) => {
  return value
    .replace(/[,\-\/ :]/g, ",") // Replace comma, period, dash, slash, space, colon with comma
    .split(",") // Split by comma
    .filter(Boolean); // Remove empty entries
};

/**
 * Adapter for MxChipInput value
 * // Replace comma, period, dash, slash, space with comma
 * @param value
 * @returns
 */
export const MxChipInputHelpAdapterValue = (value: string | string[]) => {
  if (Array.isArray(value)) {
    return value.map(v => _transform(v)).flatMap(v => v);
  }
  return _transform(value as string); // Remove empty entries
};
